/**
 * Appointment settlement is a derived view over existing records.
 *
 * Appointment
 *   └── Settlement
 *         ├── Invoice + payment transaction
 *         └── Klippekort redemption
 *
 * Do not collapse these into a single paid boolean in persisted data.
 * The UI may present a short label from this helper.
 *
 * Invoice display must follow the persisted invoice relationship:
 * Appointment → invoice_id → invoice → invoice status.
 * Do not infer that an invoice exists merely because payment_method is "invoice".
 */

export type SettlementPaymentState =
  | 'none'
  | 'issued'
  | 'pending'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'refunded'
  | 'partial'
  | 'void'
  | 'cancelled';

export type SettlementChannel = 'punch_card' | 'invoice' | 'none';

export type SettlementKind = 'none' | 'invoice_open' | 'invoice_paid' | 'punch_card';

const DRAFT_INVOICE_STATUSES = new Set(['draft']);
const PAID_INVOICE_STATUSES = new Set(['paid']);
const OUTSTANDING_INVOICE_STATUSES = new Set(['issued', 'partial', 'overdue', 'expired_pay_link']);
const TERMINAL_INVOICE_STATUSES = new Set(['void', 'cancelled', 'refunded']);
const REUSABLE_INVOICE_STATUSES = new Set([
  'draft',
  'issued',
  'partial',
  'overdue',
  'paid',
  'expired_pay_link',
]);

const PAYMENT_METHOD_ALIASES: Record<string, string> = {
  punch_card: 'punch_card',
  klippekort: 'punch_card',
  card: 'card',
  stripe: 'card',
  clinio_pay: 'card',
  kort: 'card',
  mobilepay: 'mobilepay',
  cash: 'cash',
  kontant: 'cash',
  bank: 'bank',
  bankoverførsel: 'bank',
  bankoverforsel: 'bank',
  invoice: 'invoice',
  faktura: 'invoice',
  manual: 'manual',
};

const INVOICE_LINK_RANK: Record<string, number> = {
  paid: 500,
  partial: 400,
  issued: 300,
  overdue: 300,
  expired_pay_link: 280,
  draft: 200,
  refunded: 100,
  cancelled: 50,
  void: 40,
};

export function canonicalizePaymentMethod(raw: string | null | undefined): string | null {
  const key = (raw ?? '').trim().toLowerCase();
  if (!key) return null;
  return PAYMENT_METHOD_ALIASES[key] ?? key;
}

export function paymentMethodI18nKey(canonical: string | null): string | null {
  if (!canonical) return null;
  return `appointments.payment.method_${canonical}`;
}

export function paymentStateI18nKey(state: SettlementPaymentState): string {
  switch (state) {
    case 'paid':
      return 'appointments.payment.paid';
    case 'issued':
      return 'appointments.payment.issued';
    case 'pending':
      return 'appointments.payment.pending';
    case 'processing':
      return 'appointments.payment.processing';
    case 'failed':
      return 'appointments.payment.failed';
    case 'refunded':
      return 'appointments.payment.refunded';
    case 'partial':
      return 'appointments.payment.partial';
    case 'void':
      return 'appointments.payment.void';
    case 'cancelled':
      return 'appointments.payment.cancelled';
    case 'none':
      return 'appointments.payment.none';
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

export function isCommittedInvoiceStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return !DRAFT_INVOICE_STATUSES.has(status);
}

export function isPaidInvoiceStatus(status: string | null | undefined): boolean {
  return Boolean(status && PAID_INVOICE_STATUSES.has(status));
}

export function invoiceIsTerminal(status: string | null | undefined): boolean {
  return Boolean(status && TERMINAL_INVOICE_STATUSES.has(status));
}

export function invoiceBlocksNewPaymentMethods(status: string | null | undefined): boolean {
  if (!status) return false;
  return OUTSTANDING_INVOICE_STATUSES.has(status) || PAID_INVOICE_STATUSES.has(status);
}

export function invoiceIsReusableForSettlement(status: string | null | undefined): boolean {
  return Boolean(status && REUSABLE_INVOICE_STATUSES.has(status));
}

export interface SettlementInvoiceInput {
  id: string;
  invoice_number?: string | null;
  status?: string | null;
  payment_method?: string | null;
  total_paid_cents?: number | null;
  last_sent_at?: string | null;
}

export interface SettlementPunchUsageInput {
  id: string;
  punch_card_id: string;
}

export interface SettlementInput {
  paymentStatus?: string | null;
  paymentMethod?: string | null;
  invoice?: SettlementInvoiceInput | null;
  punchUsage?: SettlementPunchUsageInput | null;
}

export interface AppointmentSettlement {
  kind: SettlementKind;
  paid: boolean;
  paymentState: SettlementPaymentState;
  method: string | null;
  channel: SettlementChannel;
  invoiceId: string | null;
  invoiceNumber: string | null;
  invoiceStatus: string | null;
  invoiceLastSentAt: string | null;
  punchCardId: string | null;
  punchUsageId: string | null;
  canStartPayment: boolean;
  /** Record another monetary payment against an open invoice. Does not start a second settlement. */
  canRecordPayment: boolean;
  canChangePaymentMethod: boolean;
}

export interface AppointmentInvoiceLinkCandidate {
  invoice_id: string;
  invoice_status: string;
  payment_method: string | null;
  created_at?: string | null;
  /** Credit notes share the visit id but are not the payable invoice. */
  kind?: string | null;
}

function emptyInvoiceFields() {
  return {
    invoiceId: null as string | null,
    invoiceNumber: null as string | null,
    invoiceStatus: null as string | null,
    invoiceLastSentAt: null as string | null,
  };
}

function normalizeState(raw: string | null | undefined): SettlementPaymentState {
  switch (raw) {
    case 'paid':
    case 'issued':
    case 'pending':
    case 'processing':
    case 'failed':
    case 'refunded':
    case 'partial':
    case 'void':
    case 'cancelled':
      return raw;
    case 'unpaid':
    case 'none':
    case '':
    case null:
    case undefined:
      return 'none';
    default:
      return 'none';
  }
}

function displayInvoice(
  invoice: SettlementInvoiceInput | null | undefined,
): SettlementInvoiceInput | null {
  if (!invoice?.id) return null;
  if (!isCommittedInvoiceStatus(invoice.status ?? null)) return null;
  return invoice;
}

function paymentStateFromInvoice(
  invoiceStatus: string | null | undefined,
  collectedCents: number,
): SettlementPaymentState {
  if (isPaidInvoiceStatus(invoiceStatus)) return 'paid';

  switch (invoiceStatus) {
    case 'partial':
      return 'partial';
    case 'refunded':
      return 'refunded';
    case 'void':
      return 'void';
    case 'cancelled':
      return 'cancelled';
    case 'issued':
    case 'overdue':
    case 'expired_pay_link': {
      if (collectedCents > 0) return 'partial';
      return 'issued';
    }
    default:
      return 'none';
  }
}

function invoiceLinkRank(status: string): number {
  return INVOICE_LINK_RANK[status] ?? 0;
}

export function preferAppointmentInvoiceLink<T extends AppointmentInvoiceLinkCandidate>(
  existing: T | undefined,
  next: T,
): T {
  if (next.kind === 'credit_note') return existing ?? next;
  if (!existing || existing.kind === 'credit_note') return next;
  const existingRank = invoiceLinkRank(existing.invoice_status);
  const nextRank = invoiceLinkRank(next.invoice_status);
  if (nextRank !== existingRank) return nextRank > existingRank ? next : existing;
  const existingTime = existing.created_at ?? '';
  const nextTime = next.created_at ?? '';
  if (nextTime !== existingTime) return nextTime > existingTime ? next : existing;
  if (next.payment_method && !existing.payment_method) return next;
  return existing;
}

export function resolveAppointmentSettlement(input: SettlementInput): AppointmentSettlement {
  const storedMethod = canonicalizePaymentMethod(input.paymentMethod);
  const invoiceMethod = canonicalizePaymentMethod(input.invoice?.payment_method);

  if (input.punchUsage || storedMethod === 'punch_card' || invoiceMethod === 'punch_card') {
    const paid = input.paymentStatus === 'paid' || Boolean(input.punchUsage);
    return {
      kind: 'punch_card',
      paid,
      paymentState: paid ? 'paid' : normalizeState(input.paymentStatus),
      method: 'punch_card',
      channel: 'punch_card',
      ...emptyInvoiceFields(),
      punchCardId: input.punchUsage?.punch_card_id ?? null,
      punchUsageId: input.punchUsage?.id ?? null,
      canStartPayment: false,
      canRecordPayment: false,
      canChangePaymentMethod: false,
    };
  }

  const invoice = displayInvoice(input.invoice);
  if (invoice) {
    const collectedCents = invoice.total_paid_cents ?? 0;
    const paymentState = paymentStateFromInvoice(invoice.status, collectedCents);
    const recordedMethod =
      storedMethod && storedMethod !== 'invoice' ? storedMethod : invoiceMethod || storedMethod;
    const method =
      paymentState === 'paid' || paymentState === 'partial' || collectedCents > 0
        ? recordedMethod || 'invoice'
        : 'invoice';
    const paid = paymentState === 'paid';
    const terminal = invoiceIsTerminal(invoice.status);

    return {
      kind: paid ? 'invoice_paid' : 'invoice_open',
      paid,
      paymentState,
      method,
      channel: 'invoice',
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoice_number ?? null,
      invoiceStatus: invoice.status ?? null,
      invoiceLastSentAt: invoice.last_sent_at ?? null,
      punchCardId: null,
      punchUsageId: null,
      canStartPayment: terminal,
      canRecordPayment:
        !paid &&
        !terminal &&
        (paymentState === 'issued' ||
          paymentState === 'pending' ||
          paymentState === 'partial' ||
          paymentState === 'failed'),
      canChangePaymentMethod: !paid && !terminal && paymentState !== 'partial' && collectedCents === 0,
    };
  }

  const paymentState = normalizeState(input.paymentStatus);
  const paid = paymentState === 'paid';
  const method = storedMethod === 'invoice' ? null : storedMethod;
  return {
    kind: paid ? 'invoice_paid' : 'none',
    paid,
    paymentState,
    method,
    channel: 'none',
    ...emptyInvoiceFields(),
    punchCardId: null,
    punchUsageId: null,
    canStartPayment: !paid,
    canRecordPayment: false,
    canChangePaymentMethod: false,
  };
}

export function settlementHasOpenInvoice(settlement: AppointmentSettlement): boolean {
  return Boolean(settlement.invoiceId);
}

export function settlementBlocksNewPaymentMethods(settlement: AppointmentSettlement): boolean {
  if (settlement.channel === 'punch_card' && settlement.paid) return true;
  return invoiceBlocksNewPaymentMethods(settlement.invoiceStatus);
}

export function settlementAllowsNewPayment(settlement: AppointmentSettlement): boolean {
  if (settlement.paid) return false;
  return !settlementBlocksNewPaymentMethods(settlement);
}

export function settlementInvoiceProgressKey(settlement: AppointmentSettlement): string | null {
  if (settlement.channel !== 'invoice') return null;
  if (settlement.paid || invoiceIsTerminal(settlement.invoiceStatus)) return null;
  if (!invoiceBlocksNewPaymentMethods(settlement.invoiceStatus)) return null;
  if (settlement.invoiceLastSentAt) return 'appointments.payment_choice.invoice_sent';
  return 'appointments.payment.invoice_issued';
}
